import { Spinner } from '@/components/loading/Spinner';
import { errorAlert, successAlert } from '@/components/others/ToastGroup';
import { DEFAULT_RPC, RPC_DEFAULT_KEY, RPC_MAPS } from '@/config';
import { MsgInfo, UserInfo } from '@/utils/types';
import {
  useGetRpcError,
  useGetRpcKey,
  useGetRpcUrl,
  usePersistActions,
} from '@/zustand-store/persist-config/selector';
import { AnchorProvider, setProvider } from '@coral-xyz/anchor';
import NodeWallet from '@coral-xyz/anchor/dist/cjs/nodewallet';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, Keypair } from '@solana/web3.js';
import { createContext, ReactNode, useEffect, useState } from 'react';
import Loading from 'react-loading';
import AOS from 'aos';
import 'aos/dist/aos.css';

export const UserContext = createContext<UserContextValue | undefined>({
  user: {} as UserInfo,
  setUser: (value: UserInfo) => {},
  login: false,
  setLogin: (value: boolean) => {},
  isLoading: false,
  setIsLoading: (value: boolean) => {},
  imageUrl: '/*.png',
  setImageUrl: (value: string) => {},
  isCreated: false,
  setIsCreated: (value: boolean) => {},
  messages: [] as MsgInfo[],
  setMessages: (value: MsgInfo[]) => {},
  coinId: '',
  setCoinId: (value: string) => {},
  newMsg: {} as MsgInfo,
  setNewMsg: (value: MsgInfo) => {},
  solPrice: 0,
  setSolPrice: (value: number) => {},
  maxPrice: 0,
  setMaxPrice: (value: number) => {},
  profileEditModal: false,
  setProfileEditModal: (value: boolean) => {},
  postReplyModal: false,
  setPostReplyModal: (value: boolean) => {},
});

export interface UserContextValue {
  user: UserInfo;
  setUser: (value: UserInfo) => void;
  login: boolean;
  setLogin: (value: boolean) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  imageUrl: string;
  setImageUrl: (value: string) => void;
  isCreated: boolean;
  setIsCreated: (value: boolean) => void;
  messages: MsgInfo[];
  setMessages: (value: MsgInfo[]) => void;
  coinId: string;
  setCoinId: (value: string) => void;
  newMsg: MsgInfo;
  setNewMsg: (value: MsgInfo) => void;
  solPrice: number;
  setSolPrice: (value: number) => void;
  maxPrice: number;
  setMaxPrice: (value: number) => void;
  profileEditModal: boolean;
  setProfileEditModal: (value: boolean) => void;
  postReplyModal: boolean;
  setPostReplyModal: (value: boolean) => void;
}

export function UserProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: UserContextValue;
}) {
  const { handleSetRpcError, handleSetRpcKey, handleSetRpcUrl } =
    usePersistActions();
  const rpcUrl = useGetRpcUrl();
  const rpcKey = useGetRpcKey();
  const rpcError = useGetRpcError();
  const [providerApp, setProviderApp] = useState(null);
  // const [connection, setConnection] = useState(null);

  // const { rpcUrl } = value;
  const wallet = useWallet();
  const publicKey = wallet?.publicKey ? wallet.publicKey.toBase58() : '';

  useEffect(() => {
    AOS.init({ once: false });
  }, []);

  useEffect(() => {
    if (
      rpcKey !== 'Custom' &&
      Object.keys(RPC_MAPS).includes(rpcKey) &&
      RPC_MAPS[rpcKey] !== rpcUrl
    ) {
      handleSetRpcUrl(RPC_MAPS[rpcKey]);
      return;
    }

    if (!Object.keys(RPC_MAPS).includes(rpcKey)) {
      handleSetRpcUrl(DEFAULT_RPC);
      handleSetRpcKey(RPC_DEFAULT_KEY);
    }
  }, []);

  useEffect(() => {
    const setAnchorProvider = async () => {
      try {
        const wsUrl = rpcUrl.replace(/^https:/, 'wss:');
        const connection = new Connection(rpcUrl, {
          commitment: 'confirmed',
          disableRetryOnRateLimit: true,
          wsEndpoint: wsUrl,
        });

        if (wallet.publicKey) {
          const provider = new AnchorProvider(connection, wallet, {
            commitment: 'confirmed',
            preflightCommitment: 'confirmed',
          });
          setProvider(provider);
          setProviderApp(provider);
        } else {
          const wallet = new NodeWallet(Keypair.generate());
          const provider = new AnchorProvider(connection, wallet, {
            commitment: 'confirmed',
            preflightCommitment: 'confirmed',
          });
          setProvider(provider);
          setProviderApp(provider);
        }

        await connection.getEpochInfo(); // check status rpc after set provider
        handleSetRpcError(false);
        if (rpcUrl !== DEFAULT_RPC && publicKey) {
          successAlert('Switch RPC successfully');
        }
      } catch (e) {
        errorAlert('RPC connect failed! Please switch RPC!');
        handleSetRpcError(true);
      }
    };

    setAnchorProvider();
  }, [rpcUrl, publicKey]);

  if (!providerApp) {
    return <Spinner />;
  }

  return (
    <UserContext.Provider value={value}> {children} </UserContext.Provider>
  );
}

export default UserContext;
